import React, { useEffect, useState } from "react";
import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  MenuItem,
  TextField,
  Typography
} from "@material-ui/core";
import { toast } from "react-toastify";

import api from "../../services/api";
import toastError from "../../errors/toastError";
import { i18n } from "../../translate/i18n";

const initialState = {
  name: "",
  category: "UTILITY",
  language: "pt_BR",
  bodyText: ""
};

const WabaTemplateModal = ({ open, onClose, whatsapp }) => {
  const [values, setValues] = useState(initialState);
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const whatsappId = whatsapp?.id;

  useEffect(() => {
    if (!open || !whatsappId) return;

    setLoading(true);
    api.get(`/waba/${whatsappId}/templates`)
      .then(({ data }) => setTemplates(data?.data || []))
      .catch(toastError)
      .finally(() => setLoading(false));
  }, [open, whatsappId]);

  const handleChange = (event) => {
    const { name, value } = event.target;
    setValues((current) => ({ ...current, [name]: value }));
  };

  const handleSubmit = async () => {
    setSaving(true);
    try {
      await api.post(`/waba/${whatsappId}/templates`, values);
      toast.success(i18n.t("connections.wabaTemplateModal.success") || "Template enviado para aprovação.");
      setValues(initialState);
      const { data } = await api.get(`/waba/${whatsappId}/templates`);
      setTemplates(data?.data || []);
    } catch (err) {
      toastError(err);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        {i18n.t("connections.wabaTemplateModal.title") || "Templates WhatsApp API"}
      </DialogTitle>
      <DialogContent dividers>
        <Grid container spacing={3}>
          <Grid item xs={12} md={5}>
            <Typography variant="subtitle2" gutterBottom>
              {i18n.t("connections.wabaTemplateModal.create") || "Criar template"}
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  name="name"
                  label={i18n.t("connections.wabaTemplateModal.name") || "Nome"}
                  value={values.name}
                  onChange={handleChange}
                  fullWidth
                  variant="outlined"
                  size="small"
                  helperText="Use letras minúsculas, números e underscores."
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  select
                  name="category"
                  label={i18n.t("connections.wabaTemplateModal.category") || "Categoria"}
                  value={values.category}
                  onChange={handleChange}
                  fullWidth
                  variant="outlined"
                  size="small"
                >
                  <MenuItem value="UTILITY">UTILITY</MenuItem>
                  <MenuItem value="MARKETING">MARKETING</MenuItem>
                  <MenuItem value="AUTHENTICATION">AUTHENTICATION</MenuItem>
                </TextField>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  select
                  name="language"
                  label={i18n.t("connections.wabaTemplateModal.language") || "Idioma"}
                  value={values.language}
                  onChange={handleChange}
                  fullWidth
                  variant="outlined"
                  size="small"
                >
                  <MenuItem value="pt_BR">pt_BR</MenuItem>
                  <MenuItem value="en_US">en_US</MenuItem>
                  <MenuItem value="es">es</MenuItem>
                </TextField>
              </Grid>
              <Grid item xs={12}>
                <TextField
                  name="bodyText"
                  label={i18n.t("connections.wabaTemplateModal.body") || "Corpo"}
                  value={values.bodyText}
                  onChange={handleChange}
                  fullWidth
                  multiline
                  minRows={5}
                  variant="outlined"
                  size="small"
                />
              </Grid>
            </Grid>
          </Grid>
          <Grid item xs={12} md={7}>
            <Typography variant="subtitle2" gutterBottom>
              {i18n.t("connections.wabaTemplateModal.list") || "Templates existentes"}
            </Typography>
            {loading ? (
              <CircularProgress size={24} />
            ) : templates.length ? (
              templates.map((template) => (
                <div key={`${template.name}-${template.language}`} style={{ padding: "10px 0", borderBottom: "1px solid rgba(0,0,0,0.08)" }}>
                  <Typography variant="body2"><strong>{template.name}</strong> ({template.language})</Typography>
                  <Typography variant="caption" color="textSecondary">
                    {template.category} - {template.status}
                  </Typography>
                </div>
              ))
            ) : (
              <Typography variant="body2" color="textSecondary">
                {i18n.t("connections.wabaTemplateModal.empty") || "Nenhum template encontrado."}
              </Typography>
            )}
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>
          {i18n.t("connections.wabaTemplateModal.close") || "Fechar"}
        </Button>
        <Button
          color="primary"
          variant="contained"
          onClick={handleSubmit}
          disabled={saving || !values.name || !values.bodyText || !whatsappId}
        >
          {saving ? <CircularProgress size={20} /> : (i18n.t("connections.wabaTemplateModal.save") || "Criar template")}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default WabaTemplateModal;
